import { Helmet } from "react-helmet-async";
import { useI18n } from "@/i18n/I18nProvider";
import { SITE_URL } from "@/lib/supabase";
import { useLocation } from "react-router-dom";

type Props = {
  title: string;
  description: string;
  path?: string;
  type?: "website" | "article";
  image?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
};

function buildAlternates(pathname: string) {
  const segs = pathname.split("/").filter(Boolean);
  const langless = segs[0] === "sv" || segs[0] === "en" ? segs.slice(1).join("/") : segs.join("/");
  const sv = `${SITE_URL}/sv${langless ? `/${langless}` : ""}`;
  const en = `${SITE_URL}/en${langless ? `/${langless}` : ""}`;
  return { sv, en, xDefault: SITE_URL + "/" };
}

export function SEO({
  title,
  description,
  type = "website",
  image,
  noindex,
  jsonLd,
}: Props) {
  const { lang } = useI18n();
  const location = useLocation();
  const pathname = location.pathname;
  noindex = noindex || Boolean(location.search);
  const canonical = `${SITE_URL}${pathname}`;
  const { sv, en, xDefault } = buildAlternates(pathname);

  if (pathname === "/") {
    title = "CasinoMenu.se — Menyn för svenska casinospelare";
    description = "Jämför casinon, licenser och betalningar i Sverige. Läs guider om spelvillkor, Spelpaus och ansvarsfullt spelande.";
  }
  const fullTitle = title.endsWith("CasinoMenu.se") ? title : `${title} | CasinoMenu.se`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonical} />
      {!noindex && <link rel="alternate" hrefLang="sv" href={sv} />}
      {!noindex && <link rel="alternate" hrefLang="en" href={en} />}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={xDefault} />}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {!noindex && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content="CasinoMenu.se" />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={image} />}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd, null, 0)}
        </script>
      )}
    </Helmet>
  );
}
